import React from "react";
import { Link } from "react-router-dom";
import { AwardIcon, BookOpenIcon, CircleIcon, CreditCardIcon, FlameIcon, LanguagesIcon, RotateCcwIcon, XIcon, CheckIcon } from "lucide-react";
import type { AppNotification, NotificationKind } from "../../types/notifications";
interface NotificationItemProps {
  item: AppNotification;
  onOpen: (item: AppNotification) => void;
  onToggleRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  compact?: boolean;
}
const kindMeta: Record<NotificationKind, {
  icon: typeof FlameIcon;
  tone: string;
  label: string;
}> = {
  streak: {
    icon: FlameIcon,
    tone: "bg-peach text-ink",
    label: "Streak"
  },
  review: {
    icon: RotateCcwIcon,
    tone: "bg-brand-tint text-ink",
    label: "Review"
  },
  translator: {
    icon: LanguagesIcon,
    tone: "bg-accent text-night",
    label: "Translator"
  },
  achievement: {
    icon: AwardIcon,
    tone: "bg-mint text-ink",
    label: "Achievement"
  },
  course: {
    icon: BookOpenIcon,
    tone: "bg-sky text-ink",
    label: "Course"
  },
  account: {
    icon: CreditCardIcon,
    tone: "bg-sunken text-ink",
    label: "Account"
  }
};
export function NotificationItem({
  item,
  onOpen,
  onToggleRead,
  onDismiss,
  compact = false
}: NotificationItemProps) {
  const meta = kindMeta[item.kind];
  const Icon = meta.icon;
  const content = <>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-line/60 ${meta.tone}`} aria-hidden="true" data-id="element-876" data-mp="k0ypz5zk7uwr">
        <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} data-id="element-877" data-mp="1gq6cohbx0a29" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-878" data-mp="1cp4abrmpbhw2">
        <span className="flex items-baseline justify-between gap-3" data-id="element-879" data-mp="1cq5t6i9yl8oc">
          <span className={`text-[15px] leading-snug text-ink ${item.read ? "font-medium" : "font-bold"}`} data-id="element-880" data-mp="17y4fcrx579w7">{item.title}</span>
          <span className="shrink-0 text-xs text-ink-2" data-id="element-881" data-mp="12zb6xdwpq92w">{item.time}</span>
        </span>
        <span className={`mt-0.5 block text-sm leading-relaxed text-ink-2 ${compact ? "line-clamp-2" : ""}`} data-id="element-882" data-mp="20g06t223r19n">{item.body}</span>
        {!compact && item.actionLabel && <span className="mt-2 inline-block text-sm font-semibold text-brand underline decoration-brand/40 underline-offset-4" data-id="element-883" data-mp="2ek8pdrianuaw">{item.actionLabel}</span>}
      </span>
    </>;
  return <li className={`group relative flex items-start gap-2 rounded-xl transition-colors duration-150 ${item.read ? "hover:bg-sunken/70" : "bg-brand-tint/45 hover:bg-brand-tint/70"}`} data-id="element-884" data-mp="25sym38hh0jt0">
      {item.href ? <Link to={item.href} onClick={() => onOpen(item)} className="focus-ring flex min-w-0 flex-1 items-start gap-3 rounded-xl p-3" data-id="element-885" data-mp="1vso6xibvl2b7">
          {content}
        </Link> : <button type="button" onClick={() => onOpen(item)} className="focus-ring flex min-w-0 flex-1 items-start gap-3 rounded-xl p-3 text-left" data-id="element-886" data-mp="1mvnnzr7krwf5">
          {content}
        </button>}

      <span className="flex shrink-0 flex-col items-center gap-1 py-3 pr-2" data-id="element-887" data-mp="3h8df616k6riu">
        {!item.read && <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-brand" aria-label="Unread" data-id="element-888" data-mp="3l7332np83rgq" />}
        {!compact && (onToggleRead || onDismiss) && <span className="flex flex-col gap-1 opacity-100 transition-opacity duration-150 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100" data-id="element-889" data-mp="15l1nx9p0bdln">
            {onToggleRead && <button type="button" onClick={() => onToggleRead(item.id)} aria-label={item.read ? "Mark as unread" : "Mark as read"} title={item.read ? "Mark as unread" : "Mark as read"} className="focus-ring grid h-8 w-8 place-items-center rounded-lg text-ink-2 hover:bg-surface hover:text-ink" data-id="element-890" data-mp="1ejvpleh72u62">
                {item.read ? <CircleIcon className="h-4 w-4" data-id="element-891" data-mp="1xchibpwt24mf" /> : <CheckIcon className="h-4 w-4" strokeWidth={2.5} data-id="element-892" data-mp="1x9bpq0oywkly" />}
              </button>}
            {onDismiss && <button type="button" onClick={() => onDismiss(item.id)} aria-label="Dismiss" title="Dismiss" className="focus-ring grid h-8 w-8 place-items-center rounded-lg text-ink-2 hover:bg-surface hover:text-ink" data-id="element-893" data-mp="2xnby1uh2o35">
                <XIcon className="h-4 w-4" data-id="element-894" data-mp="ecgt4ru5wue0" />
              </button>}
          </span>}
      </span>
    </li>;
}